/***
 *
 *   DATA TABLE
 *   The reference's three list views (ticket-list-view, contacts-list-view,
 *   team-users-table) are the same table three times: a toolbar with search +
 *   filters, a sortable bordered table on md+, stacked cards below it, and a
 *   footer with the visible range and page controls. The reference gets that
 *   from @tanstack/react-table; this is the same shape without the dependency.
 *
 *   The older `Table` in this folder derives its columns from the shape of the
 *   data, which is why those tables could not render a cell as anything but
 *   text, could not paginate, and lost their column titles whenever a row's
 *   keys changed. Columns here are declared, so a cell can be a badge, an
 *   avatar or a menu, and sorting can read a value the cell never displays.
 *
 *   PROPS
 *   columns: [{
 *     key,                unique id, also the default value accessor
 *     header,             string or node
 *     sortable,           default false
 *     sortValue(row),     value to sort on (default: row[key])
 *     searchValue(row),   text to match against (default: row[key])
 *     render(row, meta),  cell content (default: row[key])
 *     align,              left (default) / center / right
 *     width,              px number for a fixed column
 *     actions,            true for the trailing actions column (tight, right)
 *   }]
 *   rows: array of row objects
 *   rowKey(row): unique key (default: row.id)
 *   onRowClick(row): makes rows clickable + keyboard-activatable
 *   isRowClickable(row): opt a row out of the above
 *   renderCard(row, meta): mobile card body — falls back to the desktop columns
 *   loading: render skeleton rows
 *   search: show the search field
 *   searchPlaceholder
 *   filters: [{ key, label, value, onChange, options: [{ value, label }] }]
 *   onResetFilters: shows a reset button whenever search or a filter is set
 *   actions: node pinned to the right of the toolbar (e.g. an invite button)
 *   defaultSort: { key, dir } — dir is 'asc' | 'desc'
 *   pageSize: initial rows per page (default 10)
 *   pageSizeOptions: default [10, 25, 50]
 *   scroll: give the table its own scrolling viewport with a sticky head,
 *           instead of growing and letting the page scroll. Leave it off for
 *           a table with a row menu: the scroll container clips the dropdown.
 *   empty: node rendered when there are no rows at all
 *   emptyTitle / emptyText / emptyAction / emptyActionText: built-in empty row
 *
 *   `meta`, passed to both render and renderCard, is { index, count } within
 *   the current page — enough for a row menu to open upwards near the bottom.
 *
 **********/

import { useEffect, useMemo, useState } from 'react';
import { Button, Icon, Paginate, Search, Skeleton, cn } from 'components/lib';
import { useTranslate } from 'app/translations';
import Style from './dataTable.tailwind.js';

export const TABLE_PAGE_SIZE_OPTIONS = [10, 25, 50];

function isEmpty(value) {
    return value === null || value === undefined || value === '';
}

function compare(a, b) {
    if (typeof a === 'number' && typeof b === 'number') return a - b;

    return String(a).localeCompare(String(b), undefined, { numeric: true, sensitivity: 'base' });
}

function valueOf(column, row, accessor) {
    if (column[accessor]) return column[accessor](row);
    return row[column.key];
}

function SortIcon(props) {
    if (!props.direction)
        return <Icon image="chevrons-up-down" size={12} color="currentColor" className={Style.sortIcon} />;

    return (
        <Icon
            image={props.direction === 'asc' ? 'chevron-up' : 'chevron-down'}
            size={12}
            color="currentColor"
            className={Style.sortIconActive}
        />
    );
}

export function DataTable(props) {
    const { _t } = useTranslate();
    const columns = useMemo(() => (props.columns || []).filter(Boolean), [props.columns]);
    const rows = useMemo(() => props.rows || [], [props.rows]);
    const filters = useMemo(() => (props.filters || []).filter(Boolean), [props.filters]);

    const [query, setQuery] = useState('');
    // The search field owns its input state, so clearing the query from the
    // reset button has to remount it or the box keeps showing the old term.
    const [searchKey, setSearchKey] = useState(0);
    const [sort, setSort] = useState(props.defaultSort || null);
    const [pageSize, setPageSize] = useState(props.pageSize || 10);
    const [offset, setOffset] = useState(0);

    // Any change to what is being listed puts the reader back on page one —
    // otherwise filtering down to three rows leaves them staring at page four.
    const filterSignature = filters.map((f) => `${f.key}:${f.value ?? ''}`).join('|');
    useEffect(() => {
        setOffset(0);
    }, [query, filterSignature, pageSize, rows.length]);

    const searched = useMemo(() => {
        const term = query.trim().toLowerCase();
        if (!term) return rows;

        return rows.filter((row) =>
            columns.some((column) => {
                if (column.searchable === false || column.actions) return false;
                const value = valueOf(column, row, 'searchValue');
                if (value === null || value === undefined) return false;
                return String(value).toLowerCase().includes(term);
            }),
        );
    }, [rows, columns, query]);

    const sorted = useMemo(() => {
        if (!sort?.key) return searched;
        const column = columns.find((c) => c.key === sort.key);
        if (!column) return searched;

        const dir = sort.dir === 'desc' ? -1 : 1;

        return [...searched].sort((a, b) => {
            const av = valueOf(column, a, 'sortValue');
            const bv = valueOf(column, b, 'sortValue');

            // Blanks sink to the bottom in BOTH directions, so reversing the
            // sort never floats "no due date" above every real one. That means
            // they are compared outside the direction multiplier.
            if (isEmpty(av) || isEmpty(bv)) {
                if (isEmpty(av) && isEmpty(bv)) return 0;
                return isEmpty(av) ? 1 : -1;
            }

            return compare(av, bv) * dir;
        });
    }, [searched, columns, sort]);

    const page = sorted.slice(offset, offset + pageSize);
    const rowKey = props.rowKey || ((row, index) => row.id ?? index);
    const isFiltered = Boolean(query) || filters.some((f) => f.value);

    function toggleSort(key) {
        setSort((current) =>
            current?.key === key
                ? { key, dir: current.dir === 'asc' ? 'desc' : 'asc' }
                : { key, dir: 'asc' },
        );
    }

    function rowHandlers(row) {
        const clickable = props.onRowClick && (props.isRowClickable ? props.isRowClickable(row) : true);
        if (!clickable) return {};

        return {
            tabIndex: 0,
            role: 'button',
            onClick: () => props.onRowClick(row),
            onKeyDown: (e) => {
                if (e.key !== 'Enter' && e.key !== ' ') return;
                e.preventDefault();
                props.onRowClick(row);
            },
        };
    }

    const toolbar = (props.search || filters.length > 0 || props.actions) && (
        <div className={Style.toolbar}>
            <div className={Style.toolbarFilters}>
                {props.search && (
                    <Search
                        key={searchKey}
                        className={Style.search}
                        placeholder={props.searchPlaceholder || _t('table.searchPlaceholder')}
                        callback={setQuery}
                    />
                )}

                {filters.map((filter) => (
                    <label key={filter.key} className={Style.filterField}>
                        <span className={Style.filterLabel}>{filter.label}</span>
                        <select
                            className={Style.select}
                            value={filter.value ?? ''}
                            aria-label={filter.label}
                            onChange={(e) => filter.onChange(e.target.value)}>
                            {filter.options.map((option) => (
                                <option key={option.value} value={option.value}>
                                    {option.label}
                                </option>
                            ))}
                        </select>
                    </label>
                ))}

                {isFiltered && (
                    <Button
                        variant="ghost"
                        size="sm"
                        icon="rotate-ccw"
                        iconSize={14}
                        text={_t('table.filter.reset')}
                        className={Style.reset}
                        action={() => {
                            setQuery('');
                            setSearchKey((k) => k + 1);
                            props.onResetFilters?.();
                        }}
                    />
                )}
            </div>

            {props.actions && <div className={Style.toolbarActions}>{props.actions}</div>}
        </div>
    );

    if (props.loading) {
        return (
            <div className={cn(Style.wrap, props.scroll && Style.wrapScroll)}>
                {toolbar}
                <div className={Style.loading}>
                    {Array.from({ length: 6 }).map((_, i) => (
                        <Skeleton key={i} className="h-12 w-full rounded" />
                    ))}
                </div>
            </div>
        );
    }

    // Nothing to list at all — that is a page-level empty state, not a table.
    // A filter that happens to match nothing is NOT that: it keeps the table
    // (and its toolbar) so the reader can widen the filter again, and says so
    // in the body instead of offering to create the first record.
    if (!rows.length && !isFiltered && props.empty) {
        return (
            <div className={cn(Style.wrap, props.scroll && Style.wrapScroll)}>
                {toolbar}
                {props.empty}
            </div>
        );
    }

    const noResults = (
        <div className={Style.emptyCell}>
            <p className={Style.emptyTitle}>
                {isFiltered ? _t('table.noSearchResults') : props.emptyTitle || _t('result.notFound')}
            </p>
            {(isFiltered || props.emptyText) && (
                <p className={Style.emptyText}>
                    {isFiltered ? _t('table.noSearchResultsHint') : props.emptyText}
                </p>
            )}
            {!isFiltered && props.emptyAction && props.emptyActionText && (
                <div className={Style.emptyAction}>
                    <Button size="sm" variant="accent" text={props.emptyActionText} action={props.emptyAction} />
                </div>
            )}
        </div>
    );

    return (
        <div className={cn(Style.wrap, props.scroll && Style.wrapScroll, props.className)}>
            {toolbar}

            {/* Stacked cards below md — a real table does not survive a phone. */}
            <div className={cn(Style.cardsWrap, props.scroll && Style.cardsWrapScroll)}>
                <div className={Style.cards}>
                    {page.length === 0
                        ? noResults
                        : page.map((row, index) => {
                              const handlers = rowHandlers(row);

                              return (
                                  <div
                                      key={rowKey(row, index)}
                                      className={cn(Style.card, handlers.onClick && Style.cardClickable)}
                                      {...handlers}>
                                      {props.renderCard
                                          ? props.renderCard(row, { index, count: page.length })
                                          : columns
                                                .filter((c) => !c.actions)
                                                .map((column) => (
                                                    <div key={column.key} className="py-0.5">
                                                        {column.render ? column.render(row) : row[column.key]}
                                                    </div>
                                                ))}
                                  </div>
                              );
                          })}
                </div>
            </div>

            <div className={cn(Style.tableWrap, props.scroll && Style.tableWrapScroll)}>
                <table className={Style.table}>
                    <thead className={cn(Style.thead, props.scroll && Style.theadSticky)}>
                        <tr className={Style.theadRow}>
                            {columns.map((column) => {
                                const direction = sort?.key === column.key ? sort.dir : null;
                                const ariaSort = direction
                                    ? direction === 'asc'
                                        ? 'ascending'
                                        : 'descending'
                                    : undefined;

                                return (
                                    <th
                                        key={column.key}
                                        scope="col"
                                        aria-sort={ariaSort}
                                        style={column.width ? { width: column.width } : undefined}
                                        className={cn(
                                            Style.th,
                                            (column.align === 'right' || column.actions) && Style.thRight,
                                            column.align === 'center' && Style.thCenter,
                                            column.headerClassName,
                                        )}>
                                        {column.sortable ? (
                                            <button
                                                type="button"
                                                className={Style.sortButton}
                                                title={
                                                    typeof column.header === 'string'
                                                        ? _t('table.sortBy', { column: column.header })
                                                        : undefined
                                                }
                                                onClick={() => toggleSort(column.key)}>
                                                {column.header}
                                                <SortIcon direction={direction} />
                                            </button>
                                        ) : (
                                            column.header
                                        )}
                                    </th>
                                );
                            })}
                        </tr>
                    </thead>

                    <tbody>
                        {page.length === 0 ? (
                            <tr>
                                <td colSpan={columns.length}>{noResults}</td>
                            </tr>
                        ) : (
                            page.map((row, index) => {
                                const handlers = rowHandlers(row);

                                return (
                                    <tr
                                        key={rowKey(row, index)}
                                        className={cn(Style.row, handlers.onClick && Style.rowClickable)}
                                        {...handlers}>
                                        {columns.map((column) => (
                                            <td
                                                key={column.key}
                                                className={cn(
                                                    column.actions ? Style.cellActions : Style.cell,
                                                    column.align === 'right' && Style.cellRight,
                                                    column.align === 'center' && Style.cellCenter,
                                                    column.className,
                                                )}
                                                /* A row action must not also open the row. */
                                                onClick={column.actions ? (e) => e.stopPropagation() : undefined}>
                                                {column.render
                                                    ? column.render(row, { index, count: page.length })
                                                    : row[column.key]}
                                            </td>
                                        ))}
                                    </tr>
                                );
                            })
                        )}
                    </tbody>
                </table>
            </div>

            <Paginate
                total={sorted.length}
                limit={pageSize}
                offset={offset}
                onChange={setOffset}
                pageSize={pageSize}
                pageSizeOptions={props.pageSizeOptions || TABLE_PAGE_SIZE_OPTIONS}
                onPageSizeChange={setPageSize}
            />
        </div>
    );
}
